import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

import {
  Check,
  CheckCircle,
  Clock,
  Copy,
  CurrencyDollar,
  DownloadSimple,
  FileText,
  Lightning,
  Microphone,
  Robot,
  ShareNetwork,
  User,
  X
} from '@phosphor-icons/react';
/**
 * TestResultModal - Shows execution results in a modal overlay
 * Appears after the flow animation completes
 */

const TestResultModal = ({ 
  isOpen, 
  onClose, 
  result,
  metrics,
  input 
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(result || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Copied to clipboard');
  };

  const handleDownload = () => {
    const blob = new Blob([result || ''], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `test-result-${Date.now()}.md`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Downloaded');
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="w-full max-w-2xl max-h-[80vh] bg-[#12131a] border border-slate-700 rounded-2xl shadow-2xl overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/20">
                <CheckCircle size={20} className="text-emerald-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Test Complete</h3>
                <p className="text-xs text-slate-500">Execution finished successfully</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-slate-500 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Metrics Bar */}
          {metrics && (
            <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 px-6 py-3 bg-slate-900/50 border-b border-slate-800">
              <div className="flex items-center gap-2">
                <Lightning size={14} className="text-amber-400" />
                <span className="text-xs text-slate-400">Tokens:</span>
                <span className="text-xs text-white font-medium">{metrics.tokens || '—'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock size={14} className="text-blue-400" />
                <span className="text-xs text-slate-400">Time:</span>
                <span className="text-xs text-white font-medium">{metrics.duration || '—'}</span>
              </div>
              <div className="flex items-center gap-2">
                <CurrencyDollar size={14} className="text-emerald-400" />
                <span className="text-xs text-slate-400">Est. Cost:</span>
                <span className="text-xs text-white font-medium">{metrics.cost || '—'}</span>
              </div>
              {metrics.nodesExecuted && (
                <div className="flex items-center gap-2">
                  <CheckCircle size={14} className="text-purple-400" />
                  <span className="text-xs text-slate-400">Nodes:</span>
                  <span className="text-xs text-white font-medium">
                    {metrics.nodesExecuted}/{metrics.nodesTotal || metrics.nodesExecuted}
                  </span>
                </div>
              )}
              {metrics.runId && (
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-slate-500 font-mono">{metrics.runId}</span>
                </div>
              )}
            </div>
          )}

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[50vh]">
            {/* Input Summary */}
            {input && (
              <div className="mb-4 p-3 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
                <div className="flex items-start gap-2">
                  <User size={14} className="text-indigo-400 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-indigo-400 mb-1">Your Input</p>
                    <p className="text-sm text-slate-300 line-clamp-3">
                      {input.textInput?.substring(0, 200)}
                      {input.textInput?.length > 200 && '...'}
                    </p>
                    {input.files?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {input.files.map((f, i) => (
                          <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-indigo-500/20 text-xs text-indigo-300">
                            <FileText size={10} />
                            {f.name}
                          </span>
                        ))}
                      </div>
                    )}
                    {input.audioBlob && (
                      <span className="inline-flex items-center gap-1 mt-2 text-xs text-purple-400">
                        <Microphone size={10} />
                        Audio attached
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Response */}
            <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700">
              <div className="flex items-start gap-2 mb-3">
                <Robot size={14} className="text-emerald-400 mt-0.5" />
                <span className="text-xs font-medium text-emerald-400">Agent Response</span>
              </div>
              <div className="prose prose-invert prose-sm max-w-none">
                <div className="text-sm text-slate-200 whitespace-pre-wrap">
                  {result || 'No response generated'}
                </div>
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-800 bg-slate-900/50">
            <p className="text-xs text-slate-600">
              {metrics?.runId 
                ? `Run ID: ${metrics.runId}` 
                : '*Simulated response for testing purposes'}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={handleDownload}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm
                  text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
              >
                <DownloadSimple size={14} />
                <span>DownloadSimple</span>
              </button>
              <button
                onClick={handleCopy}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm
                  bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30 transition-colors"
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
                <span>{copied ? 'Copied!' : 'Copy'}</span>
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TestResultModal;
